import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Progress,
  Badge,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  useToast,
  useDisclosure,
  Divider,
  Spinner,
} from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MoreVertical, RefreshCw, Download, CreditCard, UserPlus } from 'lucide-react';
import {
  getAdminUsageSummary,
  getAdminTeam,
  updateTeamMember,
  exportUsageCSV,
  createPortalSession,
} from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import InviteModal from './InviteModal';
import FrameworkAdminViewer from './FrameworkAdminViewer';

interface UsageSummary {
  total_posts_generated: number;
  posts_by_user: { user_id: string; display_name: string; email: string; posts_generated: number; image_regens: number; estimated_cost_cents: number }[];
  posts_by_site: { site_id: string; site_name: string; posts_generated: number; image_regens: number; estimated_cost_cents: number }[];
  daily_usage: { date: string; posts_generated: number; image_regens: number; cost_cents: number }[];
  total_cost_cents: number;
  overage_posts: number;
  included_posts: number;
  billing_period_start: string | null;
  billing_period_end: string | null;
  subscription_status: string;
}

interface TeamMember {
  user_id: string;
  role: string;
  created_at: string | null;
  display_name: string;
  email: string;
  last_active_at: string | null;
  posts_generated_this_period: number;
}

export default function AdminDashboard() {
  const { userRole } = useAuth();
  const toast = useToast();
  const inviteModal = useDisclosure();
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([getAdminUsageSummary(), getAdminTeam()]);
      setSummary(s);
      setTeam(t.members || []);
    } catch {
      toast({ title: 'Failed to load admin data', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh every 60s
  useEffect(() => {
    const iv = setInterval(refresh, 60_000);
    return () => clearInterval(iv);
  }, [refresh]);

  if (loading && !summary) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" color="brand.500" />
        <Text mt={4} color="gray.500">Loading admin dashboard...</Text>
      </Box>
    );
  }

  if (!summary) return <Text p={8} color="gray.500">Unable to load admin data.</Text>;

  const pct = summary.included_posts > 0
    ? Math.min(100, Math.round((summary.total_posts_generated / summary.included_posts) * 100))
    : 0;

  const fmtDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

  const fmtCost = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const handleRoleChange = async (userId: string, role: string) => {
    try {
      await updateTeamMember(userId, { role });
      toast({ title: `Role updated to ${role}`, status: 'success', duration: 2000 });
      refresh();
    } catch {
      toast({ title: 'Failed to update role', status: 'error', duration: 3000 });
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      await updateTeamMember(userId, { remove: true });
      toast({ title: 'Member removed', status: 'success', duration: 2000 });
      refresh();
    } catch {
      toast({ title: 'Failed to remove member', status: 'error', duration: 3000 });
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportUsageCSV();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'usage-export.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Export failed', status: 'error', duration: 3000 });
    }
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between">
          <Text fontSize="xl" fontWeight="700">Admin Dashboard</Text>
          <HStack spacing={2}>
            <Button size="sm" leftIcon={<RefreshCw size={14} />} variant="ghost" onClick={refresh} isLoading={loading}>
              Refresh
            </Button>
            <Button size="sm" leftIcon={<Download size={14} />} variant="outline" onClick={handleExport}>
              Export CSV
            </Button>
            <Button
              size="sm"
              leftIcon={<CreditCard size={14} />}
              colorScheme="brand"
              onClick={async () => {
                try {
                  const { url } = await createPortalSession();
                  window.location.href = url;
                } catch { /* noop */ }
              }}
            >
              Manage Billing
            </Button>
          </HStack>
        </HStack>

        {/* Spending Overview */}
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
          <Stat bg="white" p={4} borderRadius="lg" borderWidth={1} borderColor="gray.200">
            <StatLabel>Estimated API Cost</StatLabel>
            <StatNumber>{fmtCost(summary.total_cost_cents)}</StatNumber>
            <StatHelpText>this billing period</StatHelpText>
          </Stat>
          <Stat bg="white" p={4} borderRadius="lg" borderWidth={1} borderColor="gray.200">
            <StatLabel>Posts Generated</StatLabel>
            <StatNumber>{summary.total_posts_generated} / {summary.included_posts}</StatNumber>
            <StatHelpText>
              <Progress value={pct} size="xs" colorScheme={pct >= 90 ? 'red' : 'green'} mt={1} borderRadius="full" />
            </StatHelpText>
          </Stat>
          <Stat bg="white" p={4} borderRadius="lg" borderWidth={1} borderColor="gray.200">
            <StatLabel>Overage Posts</StatLabel>
            <StatNumber>{summary.overage_posts}</StatNumber>
            <StatHelpText>above included quota</StatHelpText>
          </Stat>
          <Stat bg="white" p={4} borderRadius="lg" borderWidth={1} borderColor="gray.200">
            <StatLabel>Period</StatLabel>
            <StatNumber fontSize="md">{fmtDate(summary.billing_period_start)} — {fmtDate(summary.billing_period_end)}</StatNumber>
            <StatHelpText>
              <Badge colorScheme={summary.subscription_status === 'active' ? 'green' : 'red'}>
                {summary.subscription_status}
              </Badge>
            </StatHelpText>
          </Stat>
        </SimpleGrid>

        {/* Spending Chart */}
        {summary.daily_usage.length > 0 && (
          <Box bg="white" p={4} borderRadius="lg" borderWidth={1} borderColor="gray.200">
            <Text fontWeight="600" mb={3}>Daily Generation Volume (Last 30 Days)</Text>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={summary.daily_usage}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  fontSize={12}
                />
                <YAxis fontSize={12} />
                <Tooltip
                  labelFormatter={(d: string) => new Date(d).toLocaleDateString()}
                  formatter={(value: number, name: string) => [value, name === 'posts_generated' ? 'Posts' : name === 'image_regens' ? 'Regens' : name]}
                />
                <Bar dataKey="posts_generated" fill="#f9c65d" name="Posts" radius={[4, 4, 0, 0]} />
                <Bar dataKey="image_regens" fill="#bb9446" name="Regens" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* Usage by Team Member */}
        <Box bg="white" p={4} borderRadius="lg" borderWidth={1} borderColor="gray.200">
          <HStack justify="space-between" mb={3}>
            <Text fontWeight="600">Usage by Team Member</Text>
            <Button size="xs" leftIcon={<UserPlus size={12} />} variant="ghost" colorScheme="brand" onClick={inviteModal.onOpen}>
              Invite Member
            </Button>
          </HStack>
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Name / Email</Th>
                  <Th>Role</Th>
                  <Th isNumeric>Posts</Th>
                  <Th isNumeric>Regens</Th>
                  <Th isNumeric>Est. Cost</Th>
                  <Th>Last Active</Th>
                  {userRole === 'owner' && <Th w="40px" />}
                </Tr>
              </Thead>
              <Tbody>
                {team.map((m) => (
                  <Tr key={m.user_id}>
                    <Td>
                      <Text fontWeight="500">{m.display_name}</Text>
                      <Text fontSize="xs" color="gray.500">{m.email}</Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={m.role === 'owner' ? 'purple' : m.role === 'admin' ? 'blue' : 'gray'}>
                        {m.role}
                      </Badge>
                    </Td>
                    <Td isNumeric>{m.posts_generated_this_period}</Td>
                    <Td isNumeric>—</Td>
                    <Td isNumeric>—</Td>
                    <Td fontSize="xs" color="gray.500">
                      {m.last_active_at ? new Date(m.last_active_at).toLocaleDateString() : '—'}
                    </Td>
                    {userRole === 'owner' && (
                      <Td>
                        {m.role !== 'owner' && (
                          <Menu>
                            <MenuButton as={IconButton} icon={<MoreVertical size={14} />} variant="ghost" size="xs" />
                            <MenuList>
                              <MenuItem onClick={() => handleRoleChange(m.user_id, 'admin')}>Set Admin</MenuItem>
                              <MenuItem onClick={() => handleRoleChange(m.user_id, 'editor')}>Set Editor</MenuItem>
                              <MenuItem color="red.500" onClick={() => handleRemoveMember(m.user_id)}>Remove</MenuItem>
                            </MenuList>
                          </Menu>
                        )}
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>

        {/* Usage by Site */}
        <Box bg="white" p={4} borderRadius="lg" borderWidth={1} borderColor="gray.200">
          <Text fontWeight="600" mb={3}>Usage by Site</Text>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Site</Th>
                <Th isNumeric>Posts</Th>
                <Th isNumeric>Regens</Th>
                <Th isNumeric>Est. Cost</Th>
              </Tr>
            </Thead>
            <Tbody>
              {summary.posts_by_site.map((s) => (
                <Tr key={s.site_id}>
                  <Td fontWeight="500">{s.site_name}</Td>
                  <Td isNumeric>{s.posts_generated}</Td>
                  <Td isNumeric>{s.image_regens}</Td>
                  <Td isNumeric>{fmtCost(s.estimated_cost_cents)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        <Divider />

        {/* Script Framework viewer (Task 21) */}
        <FrameworkAdminViewer />

        <Divider />
        <Text fontSize="xs" color="gray.400" textAlign="center">
          Data refreshes automatically every 60 seconds.
        </Text>
      </VStack>

      <InviteModal isOpen={inviteModal.isOpen} onClose={inviteModal.onClose} onSuccess={refresh} />
    </Box>
  );
}
